import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  CircularProgress,
  makeStyles,
  Divider
} from "@material-ui/core";
import DatabaseActivityMetrics from "./DatabaseActivityMetrics";
import {
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Dns as DnsIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon
} from "@material-ui/icons";
import { useTimezone } from "../../../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    borderRadius: 12,
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8]
    }
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2)
  },
  cardTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600
  },
  metricValue: {
    fontSize: "2rem",
    fontWeight: 700,
    marginBottom: theme.spacing(1)
  },
  metricLabel: {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5)
  },
  progressBar: {
    height: 8,
    borderRadius: 4,
    marginTop: theme.spacing(1),
    "& .MuiLinearProgress-bar": {
      borderRadius: 4
    }
  },
  progressBarError: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#f44336"
    }
  },
  progressBarWarning: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#ff9800"
    }
  },
  progressBarSuccess: {
    "& .MuiLinearProgress-bar": {
      backgroundColor: "#4caf50"
    }
  },
  statusChip: {
    marginTop: theme.spacing(1)
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200
  },
  errorContainer: {
    padding: theme.spacing(2),
    textAlign: "center",
    color: theme.palette.error.main
  }
}));

const getStatusColor = (value, thresholds) => {
  if (!thresholds) {
    return value >= 85 ? "error" : value >= 75 ? "warning" : "success";
  }
  const { criticalThreshold, autoCleanThreshold } = thresholds;
  if (value >= criticalThreshold) return "error";
  if (value >= autoCleanThreshold) return "warning";
  return "success";
};

// Mapear colores a valores válidos de Material-UI para LinearProgress
const getProgressColor = (color) => {
  if (color === "error") return "secondary"; // secondary es rojo en Material-UI
  if (color === "warning") return "primary"; // primary puede ser naranja/amarillo según tema
  return "primary"; // success -> primary (verde se aplicará con estilo)
};

// Obtener color real para estilos inline
const getColorValue = (color) => {
  if (color === "error") return "#f44336"; // rojo
  if (color === "warning") return "#ff9800"; // naranja
  return "#4caf50"; // verde (success)
};

const getStatusIcon = (status) => {
  switch (status) {
    case "active":
    case "optimal":
      return <CheckCircleIcon style={{ color: "#4caf50" }} />;
    case "warning":
      return <WarningIcon style={{ color: "#ff9800" }} />;
    case "critical":
    case "inactive":
      return <ErrorIcon style={{ color: "#f44336" }} />;
    default:
      return <WarningIcon style={{ color: "#9e9e9e" }} />;
  }
};

const formatBytes = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const SystemStatus = ({ loading, error, systemStatus, thresholds, databaseActivity, onRefreshDatabaseActivity }) => {
  const classes = useStyles();
  const { formatDateTime } = useTimezone();

  if (loading && !systemStatus) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !systemStatus) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6" color="error">
          Error: {error}
        </Typography>
      </Box>
    );
  }

  if (!systemStatus) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="body1">
          No hay datos disponibles. Haga clic en actualizar.
        </Typography>
      </Box>
    );
  }

  // Convertir valores a números para evitar errores con toFixed
  const diskUsage = Number(systemStatus.diskUsage) || 0;
  const memoryUsage = Number(systemStatus.memoryUsage) || 0;
  const cpuUsage = Number(systemStatus.cpuUsage) || 0;
  const binaryLogsSize = Number(systemStatus.binaryLogsSize) || 0;
  const diskTotal = Number(systemStatus.diskTotal) || 0;
  const diskUsed = Number(systemStatus.diskUsed) || 0;
  const diskAvailable = Number(systemStatus.diskAvailable) || 0;
  const memoryTotal = Number(systemStatus.memoryTotal) || 0;
  const memoryUsed = Number(systemStatus.memoryUsed) || 0;

  const diskColor = getStatusColor(diskUsage, thresholds);
  const memoryColor = getStatusColor(memoryUsage, thresholds);
  const cpuColor = cpuUsage >= 80 ? "error" : cpuUsage >= 60 ? "warning" : "success";

  return (
    <Grid container spacing={3}>
      {/* Card: Disco */}
      <Grid item xs={12} md={6} lg={4}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <Typography variant="h6" className={classes.cardTitle}>
                <StorageIcon color="primary" />
                Disco
              </Typography>
              <Chip
                label={systemStatus.systemStatus || "unknown"}
                size="small"
                className={classes.statusChip}
                style={{
                  backgroundColor: diskColor === "error" ? "#f44336" : diskColor === "warning" ? "#ff9800" : "#4caf50",
                  color: "#fff"
                }}
              />
            </Box>
            <Typography 
              variant="h4" 
              className={classes.metricValue} 
              style={{ color: getColorValue(diskColor) }}
            >
              {diskUsage.toFixed(1)}%
            </Typography>
            <Typography variant="body2" className={classes.metricLabel}>
              Total: {formatBytes(diskTotal)}
            </Typography>
            <Typography variant="body2" className={classes.metricLabel}>
              Usado: {formatBytes(diskUsed)} | Disponible: {formatBytes(diskAvailable)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={diskUsage}
              color={getProgressColor(diskColor)}
              className={`${classes.progressBar} ${
                diskColor === "error" ? classes.progressBarError :
                diskColor === "warning" ? classes.progressBarWarning :
                classes.progressBarSuccess
              }`}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Card: Binary Logs */}
      <Grid item xs={12} md={6} lg={4}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <Typography variant="h6" className={classes.cardTitle}>
                <DnsIcon color="secondary" />
                Binary Logs
              </Typography>
            </Box>
            <Typography variant="h4" className={classes.metricValue}>
              {binaryLogsSize.toFixed(2)} GB
            </Typography>
            <Typography variant="body2" className={classes.metricLabel}>
              Archivos: {systemStatus.binaryLogsCount || 0}
            </Typography>
            {thresholds && (
              <Typography variant="body2" className={classes.metricLabel}>
                Umbral: {Number(thresholds.binlogSizeThreshold) || 0} GB
              </Typography>
            )}
            {thresholds && binaryLogsSize >= Number(thresholds.binlogSizeThreshold) && (
              <Chip
                label="⚠️ Sobre umbral"
                color="warning"
                size="small"
                className={classes.statusChip}
              />
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Card: Memoria */}
      <Grid item xs={12} md={6} lg={4}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <Typography variant="h6" className={classes.cardTitle}>
                <MemoryIcon color="primary" />
                Memoria
              </Typography>
            </Box>
            <Typography 
              variant="h4" 
              className={classes.metricValue} 
              style={{ color: getColorValue(memoryColor) }}
            >
              {memoryUsage.toFixed(1)}%
            </Typography>
            <Typography variant="body2" className={classes.metricLabel}>
              Total: {formatBytes(memoryTotal)}
            </Typography>
            <Typography variant="body2" className={classes.metricLabel}>
              Usada: {formatBytes(memoryUsed)}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={memoryUsage}
              color={getProgressColor(memoryColor)}
              className={`${classes.progressBar} ${
                memoryColor === "error" ? classes.progressBarError :
                memoryColor === "warning" ? classes.progressBarWarning :
                classes.progressBarSuccess
              }`}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Card: CPU */}
      <Grid item xs={12} md={6} lg={4}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <Typography variant="h6" className={classes.cardTitle}>
                <SpeedIcon color="primary" />
                CPU
              </Typography>
            </Box>
            <Typography 
              variant="h4" 
              className={classes.metricValue} 
              style={{ color: getColorValue(cpuColor) }}
            >
              {cpuUsage.toFixed(1)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={cpuUsage}
              color={getProgressColor(cpuColor)}
              className={`${classes.progressBar} ${
                cpuColor === "error" ? classes.progressBarError :
                cpuColor === "warning" ? classes.progressBarWarning :
                classes.progressBarSuccess
              }`}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Card: Servicios */}
      <Grid item xs={12} md={6} lg={4}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <Typography variant="h6" className={classes.cardTitle}>
                Servicios
              </Typography>
            </Box>
            <Box style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {getStatusIcon(systemStatus.mysqlStatus)}
                <Typography variant="body1">MySQL: {systemStatus.mysqlStatus || "unknown"}</Typography>
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {getStatusIcon(systemStatus.redisStatus)}
                <Typography variant="body1">Redis: {systemStatus.redisStatus || "unknown"}</Typography>
              </Box>
              <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <CheckCircleIcon style={{ color: "#4caf50" }} />
                {/* -1 = el backend corre en Docker y no usa PM2 (doc 0434, M3) */}
                <Typography variant="body1">
                  {systemStatus.pm2AppsCount < 0 ? "PM2: no aplica (Docker)" : `PM2 Apps: ${systemStatus.pm2AppsCount || 0} online`}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {/* Card: Información del Servidor */}
      <Grid item xs={12} md={6} lg={4}>
        <Card className={classes.card}>
          <CardContent>
            <Box className={classes.cardHeader}>
              <Typography variant="h6" className={classes.cardTitle}>
                Servidor
              </Typography>
            </Box>
            <Typography variant="body2" className={classes.metricLabel}>
              Nombre: {systemStatus.serverName || "N/A"}
            </Typography>
            <Typography variant="body2" className={classes.metricLabel}>
              IP: {systemStatus.serverIP || "N/A"}
            </Typography>
            {systemStatus.updatedAt && (
              <Typography variant="body2" className={classes.metricLabel}>
                Última actualización: {formatDateTime(systemStatus.updatedAt)}
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      {/* Sección: Actividad de Base de Datos */}
      <Grid item xs={12}>
        <Divider style={{ marginTop: 24, marginBottom: 24 }} />
        <DatabaseActivityMetrics
          loading={loading}
          error={error}
          data={databaseActivity}
          onRefresh={onRefreshDatabaseActivity}
        />
      </Grid>
    </Grid>
  );
};

export default SystemStatus;
